import EventIcon from "@mui/icons-material/Event";
import { DialogActions, DialogContent } from "@mui/material";
import {
  Autocomplete,
  Button,
  Checkbox,
  CreatedUpdatedBy,
  DateTimePicker,
  Field,
  FieldsContainer,
  Form,
  PageHeader,
  TextField,
} from "common";
import { ParamsContext } from "common/components/Form/ParamsContext";
import { EventItem } from "features/events/eventApi";
import HistoryListDialog from "features/histories/HistoryListDialog";
import useAccess from "features/profile/useAccess";
import I18n from "i18n";
import moment from "moment";
import { createContext } from "react";
import useEventGroupForm, {
  UseEventGroupFormProps,
  UseEventGroupFormReturnType,
} from "./useEventGroupForm";
export const EventGroupContext = createContext<
  UseEventGroupFormReturnType | undefined
>(undefined);
export interface EventGroupFormProps extends UseEventGroupFormProps {
  eventToMove?: EventItem;
}

export default function EventGroupForm({
  id,
  afterSave,
  onClose,
  defaultValues,
  eventGroupIds,
  eventToMove,
}: EventGroupFormProps) {
  const { hasPermissions, canEditItem } = useAccess();
  const form = useEventGroupForm({
    id,
    afterSave,
    defaultValues,
    eventGroupIds,
  });
  const {
    params,
    item,
    options,
    errors,
    loading,
    isDirty,
    isCreate,
    isUpdate,
    getIsDirty,
    getRequiredFields,
    onChange,
    save,
    saving,
    selectedBoat,
    groupEdit,
  } = form;
  const title = groupEdit
    ? I18n.t("EventGroup.editTitle")
    : isCreate
    ? I18n.t("EventGroup.addTitle")
    : item.start_time
    ? moment(item.start_time).format("L LT")
    : I18n.t("EventGroup.editTitle");
  let subtitle = selectedBoat ? selectedBoat.name : "Nincs hajó kiválasztva";
  if (eventToMove) {
    subtitle = `${eventToMove?.program?.name}: ${eventToMove?.boat?.name}, ${eventToMove?.start_time}  -> ${selectedBoat?.name}, ${item.start_time}`;
  }
  return (
    <ParamsContext.Provider value={{ event_group_id: id }}>
      <EventGroupContext.Provider value={form}>
        <PageHeader
          avatar={<EventIcon />}
          title={title}
          subtitle={subtitle}
          onClose={onClose}
          loading={loading}
        />
        <DialogContent>
          <Form
            loading={loading}
            item={item}
            options={options}
            errors={errors}
            onChange={onChange}
            translationCategory="EventGroup.attributes"
            translationPlaceholdersCategory="EventGroup.placeholders"
            translationHelpersCategory="EventGroup.helpers"
            isDirty={getIsDirty}
            requiredFields={getRequiredFields()}
            disabled={!canEditItem("event_group", item)}
          >
            <FieldsContainer>
              {!groupEdit && (
                <>
                  <Field fullWidth>
                    <Autocomplete id="boat_id" />
                  </Field>
                  <Field fullWidth>
                    <DateTimePicker id="start_time" />
                  </Field>
                </>
              )}
              <Field fullWidth>
                <TextField id="duration" mask={Number} />
              </Field>
              <Field fullWidth>
                <TextField id="number_of_places" mask={Number} />
              </Field>
              <Field fullWidth>
                <TextField id="number_of_online_places" mask={Number} />
              </Field>
              <Field fullWidth>
                <TextField id="places_tolerance" mask={Number} />
              </Field>
              {!groupEdit && (
                <Field fullWidth>
                  <Checkbox id="closed" />
                  <Checkbox id="online_closed" />
                  <Checkbox id="low_cancelled" />
                </Field>
              )}
            </FieldsContainer>
          </Form>
        </DialogContent>
        <DialogActions>
          {isUpdate && (
            <CreatedUpdatedBy
              objectName="EventGroup"
              item={item}
              sx={{
                p: 1,
                paddingTop: 0,
                paddingBottom: 0.5,
                marginRight: "auto",
              }}
              renderDialog={
                hasPermissions(["history.viewany", "history.viewown"])
                  ? props => <HistoryListDialog {...props} subtitle={title} />
                  : undefined
              }
            />
          )}
          <Button
            title={I18n.t("App.save")}
            disabled={!isDirty}
            onClick={() => save()}
            loading={saving}
          />
        </DialogActions>
      </EventGroupContext.Provider>
    </ParamsContext.Provider>
  );
}
